/**
 * Mapping utility functions
 */

// Calculate distance between two points using the Haversine formula
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in km
  return distance;
};

// Convert degrees to radians
export const deg2rad = (deg: number): number => {
  return deg * (Math.PI / 180);
};

// Get color based on profession
export const getProfessionColor = (profession: string | undefined): string => {
  if (!profession) return '#3388ff'; // Default blue color

  const professionLower = profession.toLowerCase();
  const colorMap: Record<string, string> = {
    'technology': '#3498db',
    'finance': '#2ecc71',
    'healthcare': '#9b59b6',
    'education': '#e74c3c',
    'engineering': '#f39c12',
    'legal': '#1abc9c',
    'marketing': '#34495e',
    'sales': '#16a085',
    'human resources': '#d35400',
    'arts': '#8e44ad',
    'manufacturing': '#27ae60',
    'retail': '#2980b9',
    'government': '#c0392b',
    'energy': '#f1c40f',
    'student': '#95a5a6',
    'retired': '#7f8c8d',
    'other': '#2c3e50'
  };

  // Try to find an exact match first
  if (colorMap[professionLower]) {
    return colorMap[professionLower];
  }

  // If no exact match, look for partial matches
  for (const key in colorMap) {
    if (professionLower.includes(key) || key.includes(professionLower)) {
      return colorMap[key];
    }
  }

  // Default fallback
  return '#3388ff';
};

// Get marker size based on experience level
export const getMarkerSize = (level: string | undefined): number => {
  if (!level) return 20; // Default size

  const levelLower = level.toLowerCase();
  const sizeMap: Record<string, number> = {
    'junior': 20,
    'mid-level': 25,
    'midlevel': 25,
    'mid': 25,
    'senior': 30,
    'expert': 35
  };

  // Try to find an exact match first
  if (sizeMap[levelLower]) {
    return sizeMap[levelLower];
  }

  // If no exact match, look for partial matches
  for (const key in sizeMap) {
    if (levelLower.includes(key) || key.includes(levelLower)) {
      return sizeMap[key];
    }
  }

  // Numerical experience parsing (e.g. "5 years")
  const yearsMatch = levelLower.match(/\d+/);
  if (yearsMatch) {
    const years = parseInt(yearsMatch[0]);
    if (years < 4) return sizeMap['junior'];
    if (years < 8) return sizeMap['midlevel'];
    return sizeMap['senior'];
  }

  // Default fallback
  return 20;
};

// Add random offset to location coordinates to avoid exact overlaps
export const addLocationOffset = (lat: number, lng: number): [number, number] => {
  // Random offset between -0.0005 and 0.0005 degrees (roughly 50 meters)
  const latOffset = (Math.random() - 0.5) * 0.001;
  const lngOffset = (Math.random() - 0.5) * 0.001;
  
  return [lat + latOffset, lng + lngOffset];
};
